// Resposta de um tópico do FAQ da página de contato em texto corrido, para a
// tool WebMCP `get_help_answer`. Os textos chegam por parâmetro (os de
// tradutors/faq.ts, no idioma da rota): sem imports, roda no Nuxt e no
// node:test.
//
// Cada resposta repete o que o componente do tópico desenha, na mesma ordem.
// O link vai à parte, como destino nomeado: quem chama resolve a URL pelo
// router.

/** Tópicos na ordem do `labelFaq` (tradutors/contact.ts) e do CollapseBox. */
export const TOPICOS_DO_FAQ = [
  "renew",
  "upgrade",
  "upgradeBonus",
  "logout",
  "statusAccount",
  "consultCredits",
  "buyCredits",
  "forgotPassword",
  "photos",
  "sugestion",
  "cancel",
  "contact",
] as const;

export type TopicoDoFaq = (typeof TOPICOS_DO_FAQ)[number];

/** Para onde a resposta manda a pessoa. `semIdioma`: rota fora de /[language]. */
export interface DestinoDoFaq {
  name: string;
  query?: Record<string, string>;
  semIdioma?: boolean;
}

export interface RespostaDoFaq {
  texto: string;
  destino: DestinoDoFaq | null;
}

type TextosDoTopico = Record<string, string | undefined>;

/** Junta pedaços de uma mesma frase, como o template faz com o espaço entre eles. */
function frase(...partes: Array<string | undefined>): string {
  return partes
    .map((parte) => (parte ?? "").trim())
    .filter(Boolean)
    .join(" ");
}

function linhas(...partes: Array<string | undefined>): string {
  return partes
    .map((parte) => (parte ?? "").trim())
    .filter(Boolean)
    .join("\n");
}

function itens(...partes: Array<string | undefined>): string {
  return partes
    .map((parte) => (parte ?? "").trim())
    .filter(Boolean)
    .map((parte) => `- ${parte}`)
    .join("\n");
}

const MINHA_CONTA = { name: "language-minhaconta" };
// Renovação e upgrade vão direto para a aba deles: o `tab=0` do link do site
// abre a Área GOLD para quem é Gold (utils/accountTabs.ts).
const RENOVAR = { name: "language-minhaconta", query: { tab: "renew" } };
const CONTATO = { name: "language-contato" };

export function respostaDoFaq(
  topico: TopicoDoFaq,
  textos: Record<string, TextosDoTopico>,
): RespostaDoFaq {
  const t = textos[topico] ?? {};

  switch (topico) {
    case "renew":
      return { texto: frase(t.acesse, t.aqui), destino: RENOVAR };
    case "statusAccount":
      return { texto: frase(t.acesse, t.aqui), destino: MINHA_CONTA };
    case "upgrade":
      return {
        texto: linhas(
          t.title,
          frase(t.intro, t.linkText, t.afterLink),
          itens(t.bonus, t.bonusValue, t.confirm),
          t.plansTitle,
          t.basicDesc,
          itens(frase(t.plusTitle, "—", t.plusDesc), frase(t.goldTitle, "—", t.goldDesc)),
        ),
        destino: RENOVAR,
      };
    case "upgradeBonus":
      return {
        texto: linhas(
          t.title,
          t.intro,
          itens(frase(t.account, t.linkText) + ".", t.bonus, t.bonusValue, t.confirm),
          t.final,
        ),
        destino: RENOVAR,
      };
    case "logout":
      // O "reiniciar acesso" é um botão do próprio tópico (faz logout).
      return { texto: linhas(t.title, t.intro, t.linkText), destino: CONTATO };
    case "consultCredits":
      return {
        texto: linhas(t.title, t.intro, t.linkText),
        destino: { name: "language-minhaconta", query: { tab: "1" } },
      };
    case "buyCredits":
      return {
        texto: linhas(t.title, t.intro, t.linkText),
        destino: { name: "language-assine", query: { tab: "credits" } },
      };
    case "photos":
      return {
        texto: linhas(t.title, t.text1, t.text2, t.text3, frase(t.text4, t.textLink)),
        destino: { name: "limpar-cache", semIdioma: true },
      };
    case "cancel":
      return {
        texto: linhas(frase(t.title, t.intro), t.linkText),
        destino: { name: "language-cancelform" },
      };
    case "forgotPassword":
    case "sugestion":
    case "contact":
      return { texto: frase(t.answer), destino: CONTATO };
  }
}
